import React, {
    PropsWithChildren,
    useContext,
    useEffect,
    useState,
} from "react";
import { useIntl } from "react-intl";
import { useFeatureFlag } from "@evidenceb/athena-common/modules/FeatureFlags";
import {
    getInformationBanner,
    getItemDescription,
    getObjectiveById,
    getResourceIndex,
} from "../../utils/dataRetrieval";
import ModuleAccordion from "./ModuleAccordion/ModuleAccordion";
import PageWrapper from "../../components/PageWrapper/PageWrapper";
import PageHeader from "../../components/PageHeader/PageHeader";
import { getWorkmodeNaming } from "../Workmodes/workmodesMessages";
import { commonMessages, pageTitles } from "../../utils/messages";
import ContainerBlock from "../../design-system-components/ContainerBlock/ContainerBlock";
import { BaseRouteWithHeaderWrapper } from "../../components/RouteWithHeaderWrapper/RouteWithHeaderWrapper";
import { useData } from "../../hooks/useData";
import { useUrl } from "../../hooks/usePage";
import { Pagetype } from "../../interfaces/Config";
import { WorkModeType } from "@evidenceb/athena-common/interfaces/Workmode";
import { useActiveSubject } from "../../hooks/useActiveSubject";
import { Filter } from "../../design-system-components/ResourceFilter/Components/filterTypes";
import { useSafeMappingTree } from "../../hooks/useMappingTree";
import { filterModules } from "../SoloAIModuleList/utils";
import ResourceMappingFiltersControlBar from "../../design-system-components/ResourceFilter/ResourceMappingFiltersControlBar";
import { useResourceFilterFlag } from "../../hooks/useResourceFilterFlag";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";
import { useWorkmodeConfig } from "../../hooks/useConfig";
import { UserType } from "../../interfaces/User";
import WithAiInformationBanner from "./InformationBanners/WithAiInformationBanner";
import { withExercises } from "../../hooks/queries/useExercisesQuery";

import "./TeacherModuleList.scss";
import WithoutAiInformatonBanner from "./InformationBanners/WithoutAiInformatonBanner";
import InformationBanner from "../../design-system-components/InformationBanner/InformationBanner";
import { configStore } from "../../contexts/ConfigContext";
import { InformationBannerType } from "../../design-system-components/InformationBanner/type";
import { Data } from "../../interfaces/Data";

const TeacherModuleList = () => {
    const intl = useIntl();
    const data = useData();
    const teacherModuleListShowFullTitle = useFeatureFlag(
        "teacherModuleListShowFullTitle"
    );
    const [openModuleId, setOpenModuleId] = useState<string | null>(null);

    const toggleAccordion = (id: string) => {
        setOpenModuleId(id === openModuleId ? null : id);
    };

    const activeSubject = useActiveSubject();
    const [appliedFilters, setAppliedFilters] = useState<Filter[]>([]);
    useEffect(() => {
        // Reset when active subject changes
        setAppliedFilters([]);
    }, [activeSubject]);
    const mappingTree = useSafeMappingTree();
    const filteredModules = mappingTree
        ? filterModules(data, appliedFilters, mappingTree)
        : data.modules;

    return (
        <Wrapper
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            data={data}
        >
            <main>
                <ContainerBlock className="teacher-module-list">
                    <ul className="teacher-module-list__list">
                        {filteredModules.map((module) => (
                            <li key={module.id}>
                                <ModuleAccordion
                                    module={module}
                                    title={
                                        teacherModuleListShowFullTitle
                                            ? `${getResourceIndex(
                                                  module,
                                                  data,
                                                  "module"
                                              )}. ${module.title.short!}`
                                            : `${intl.formatMessage(
                                                  commonMessages.module
                                              )} ${getResourceIndex(
                                                  module,
                                                  data,
                                                  "module"
                                              )}`
                                    }
                                    activeSubject={activeSubject}
                                    description={getItemDescription(
                                        module,
                                        "teacher"
                                    )}
                                    objectives={module.objectiveIds.map(
                                        (objectiveId) =>
                                            getObjectiveById(
                                                objectiveId,
                                                data,
                                                false
                                            )
                                    )}
                                    open={module.id === openModuleId}
                                    onToggle={toggleAccordion}
                                    filters={appliedFilters}
                                />
                            </li>
                        ))}
                    </ul>
                </ContainerBlock>
            </main>
        </Wrapper>
    );
};
export default withExercises(TeacherModuleList, {
    ifOfflineFeaturesActive: true, // Exercise content is only needed for the assets cache
});

const Wrapper = ({
    children,
    appliedFilters,
    setAppliedFilters,
    data,
}: PropsWithChildren & {
    appliedFilters: Filter[];
    setAppliedFilters: React.Dispatch<React.SetStateAction<Filter[]>>;
    data: Data;
}) => {
    const intl = useIntl();
    const title = intl.formatMessage(pageTitles.teacherSoloAIModuleList);
    const workModeSelectionUrl = useUrl(Pagetype.WORK_MODES);
    const { isMobile } = useBreakpoints();
    const workmodeConfig = useWorkmodeConfig(WorkModeType.SoloAI);
    const { nameMessage } = getWorkmodeNaming(
        workmodeConfig!,
        UserType.Teacher
    );
    const {
        config: {
            features: { informationBanners },
        },
    } = useContext(configStore);
    const teacherModuleListBanner =
        informationBanners?.length &&
        getInformationBanner(
            InformationBannerType.TEACHER_MODULE_LIST,
            informationBanners
        );
    const resourceFilterPanelFlag = useResourceFilterFlag("teacherModuleList");
    return (
        <BaseRouteWithHeaderWrapper title={title}>
            {!isMobile && (
                <>
                    {teacherModuleListBanner && (
                        <InformationBanner data={teacherModuleListBanner} />
                    )}
                    <WithoutAiInformatonBanner />
                    <WithAiInformationBanner />
                </>
            )}
            <PageWrapper>
                <PageHeader
                    title={{
                        content: (
                            <>
                                <h1>{intl.formatMessage(nameMessage)}</h1>
                                {resourceFilterPanelFlag && (
                                    <ResourceMappingFiltersControlBar
                                        filtersModalTitle={intl.formatMessage(
                                            pageTitles.teacherSoloAIModuleList
                                        )}
                                        appliedFilters={appliedFilters}
                                        onAppliedFilterChange={
                                            setAppliedFilters
                                        }
                                        resources={data.objectives}
                                        className="teacher-module-list__filters"
                                    />
                                )}
                            </>
                        ),
                    }}
                    backTo={workModeSelectionUrl}
                />
                {isMobile && (
                    <>
                        {teacherModuleListBanner && (
                            <InformationBanner data={teacherModuleListBanner} />
                        )}
                        <WithoutAiInformatonBanner />
                        <WithAiInformationBanner />
                    </>
                )}
                {children}
            </PageWrapper>
        </BaseRouteWithHeaderWrapper>
    );
};
